import React, { useMemo, useState } from "react"
import ActionCard from "./ActionCard"
import WebhookCard from "./WebhookCard"
import { ActionIcon, RemoveButton, Step, card, field, label, link } from "./styles"

const t = key => I18n.t(`automations.${key}`)

let counter = 0
const newKey = () => `new-${Date.now()}-${counter++}`

const blankAction = (type, props) => ({
  key: newKey(), id: null, type, roles: [], userIds: [], emails: "", subject: "", message: "", body: "",
  activityType: props.activityTypes[0]?.value || "", dueInDays: "", priority: props.priorities[0]?.value || ""
})

const blankCondition = spec => ({
  field: spec.key, operator: spec.operators[0].value, value: spec.type === "select" ? (spec.options[0]?.value || "") : ""
})

const formFields = (state, props) => {
  const f = [["automation[name]", state.name], ["automation[event]", state.event],
             ["automation[active]", state.active ? "1" : "0"], ["automation[conditions][]", ""]]

  state.conditions.forEach(c => {
    f.push(["automation[conditions][][field]", c.field], ["automation[conditions][][operator]", c.operator], ["automation[conditions][][value]", c.value])
  })
  if (props.legacyAllowed) f.push(["automation[legacy_conditions]", state.legacyConditions])

  state.actions.forEach((action, index) => {
    const name = key => `automation[actions_attributes][${index}][${key}]`
    if (action.destroyed && !action.id) return
    if (action.id) f.push([name("id"), action.id])
    if (action.destroyed) return f.push([name("_destroy"), "1"])

    f.push([name("action_type"), action.type], [name("position"), index], [`${name("roles")}[]`, ""], [`${name("user_ids")}[]`, ""])
    action.roles.forEach(role => f.push([`${name("roles")}[]`, role]))
    action.userIds.forEach(id => f.push([`${name("user_ids")}[]`, id]))
    ;[["emails", "emails"], ["subject", "subject"], ["message", "message"], ["body", "body"],
      ["activity_type", "activityType"], ["due_in_days", "dueInDays"], ["priority", "priority"]]
      .forEach(([param, key]) => f.push([name(param), action[key]]))
  })

  if (props.webhookAllowed) {
    const w = state.webhook
    f.push(["automation[webhook_enabled]", w.enabled ? "1" : "0"])
    if (w.enabled) {
      f.push(["automation[webhook_url]", w.url], ["automation[http_method]", w.httpMethod],
             ["automation[authentication_method]", w.authenticationMethod],
             ["automation[authentication_method_basic_user]", w.basicUser], ["automation[authentication_method_basic_password]", w.basicPassword],
             ["automation[authentication_method_bearer_token]", w.bearerToken], ["automation[authentication_method_oauth_url]", w.oauthUrl],
             ["automation[authentication_method_oauth_client]", w.oauthClient], ["automation[authentication_method_oauth_secret]", w.oauthSecret],
             ["automation[payload_template]", w.payloadTemplate], ["automation[filterable]", w.filterable ? "1" : "0"],
             ["automation[is_redirection]", w.isRedirection ? "1" : "0"], ["automation[custom_headers][]", ""])
      w.customHeaders.forEach(h => f.push(["automation[custom_headers][][name]", h.name], ["automation[custom_headers][][value]", h.value]))
    }
  }
  return f
}

const AutomationForm = props => {
  const [state, setState] = useState(() => ({ ...props.automation, actions: props.automation.actions.map(a => ({ ...a, destroyed: false })) }))
  const [legacyOpen] = useState(() => !!props.automation.legacyConditions)
  const set = change => setState(s => ({ ...s, ...change }))

  const types      = useMemo(() => Object.fromEntries(props.actionTypes.map(type => [type.value, type])), [])
  const eventSpec  = props.events[state.event] || { object: null, actions: [], webhook: false, manual: false }
  const objectSpec = props.objects[eventSpec.object] || { roles: [], fields: [] }
  const fieldSpec  = key => objectSpec.fields.find(spec => spec.key === key)
  const liveActions = state.actions.filter(a => !a.destroyed)

  const changeEvent = event => setState(s => {
    const spec   = props.events[event] || { object: null, actions: [], webhook: false }
    const object = props.objects[spec.object] || { roles: [], fields: [] }
    const fits   = c => {
      const field = object.fields.find(f => f.key === c.field)
      if (!field || field.type !== "select" || field.options.some(o => String(o.value) === String(c.value))) return c
      return { ...c, value: field.options[0]?.value || "" }
    }
    const action = a => {
      if (!spec.actions.includes(a.type)) return a.destroyed ? a : { ...a, destroyed: true, hiddenByEvent: true }
      const shown = a.hiddenByEvent ? { ...a, destroyed: false, hiddenByEvent: false } : a
      return { ...shown, roles: shown.roles.filter(r => object.roles.includes(r)) }
    }
    const w = s.webhook
    return {
      ...s,
      event,
      conditions: s.conditions.filter(c => object.fields.some(f => f.key === c.field)).map(fits),
      actions: s.actions.map(action),
      webhook: spec.webhook ? (w.hiddenByEvent ? { ...w, enabled: true, hiddenByEvent: false } : w)
                            : (w.enabled ? { ...w, enabled: false, hiddenByEvent: true } : w)
    }
  })

  const updateCondition = (index, change) => set({ conditions: state.conditions.map((c, i) => (i === index ? { ...c, ...change } : c)) })
  const updateAction    = (key, change) => set({ actions: state.actions.map(a => (a.key === key ? { ...a, ...change } : a)) })
  const removeAction    = key => set({ actions: state.actions.map(a => (a.key === key ? { ...a, destroyed: true } : a)).filter(a => a.id || !a.destroyed) })

  const eventLabel = props.eventGroups.flatMap(g => g.events).find(e => e.value === state.event)?.label || ""
  const conditionText = c => {
    const spec  = fieldSpec(c.field)
    if (!spec) return ""
    const shown = spec.type === "select" ? spec.options.find(o => String(o.value) === String(c.value))?.label : c.value
    return `${spec.label} ${spec.operators.find(o => o.value === c.operator)?.label || ""} ${shown || "…"}`.toLowerCase()
  }
  const actionNames = [...(state.webhook.enabled ? ["webhook"] : []), ...liveActions.map(a => a.type)].map(type => types[type].label.toLowerCase())

  return (
    <form method="post" action={props.url} acceptCharset="UTF-8" className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_250px]">
      <input type="hidden" name="authenticity_token" value={props.csrfToken} />
      {props.method === "patch" && <input type="hidden" name="_method" value="patch" />}
      {formFields(state, props).map(([name, value], index) => <input key={index} type="hidden" name={name} value={value ?? ""} />)}

      <div className="space-y-5">
        {props.errors.length > 0 && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 !px-4 !py-3 text-[12.5px] text-rose-700">
            {props.errors.map(message => <div key={message}>{message}</div>)}
          </div>
        )}

        <Step tone="bg-amber-100 text-amber-600" icon="fa-bolt" title={t("form.when_title")} hint={t("form.when_hint")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <span className={label}>{t("form.event")}</span>
              <select className={field} value={state.event} disabled={props.eventLocked} onChange={e => changeEvent(e.target.value)}>
                {props.eventGroups.map(group => (
                  <optgroup key={group.label} label={group.label}>
                    {group.events.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <span className={label}>{t("form.name")}</span>
              <input type="text" required className={field} value={state.name} placeholder={t("form.name_placeholder")}
                     onChange={e => set({ name: e.target.value })} />
            </div>
          </div>
          <label className="!mt-4 inline-flex cursor-pointer items-center gap-2 text-[12.5px] text-slate-600">
            <input type="checkbox" className="h-4 w-4 rounded accent-blue-600" checked={state.active} disabled={props.eventLocked} onChange={e => set({ active: e.target.checked })} />
            {t("form.active")}
          </label>
        </Step>

        <Step tone="bg-sky-100 text-sky-600" icon="fa-filter" title={t("form.if_title")} hint={t("form.if_hint")}>
          <div className="space-y-2">
            {state.conditions.map((condition, index) => {
              const spec = fieldSpec(condition.field)
              if (!spec) return null
              return (
                <div key={index} className="flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 !p-2">
                  <span className={`!px-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 ${index === 0 ? "invisible" : ""}`}>{t("form.and")}</span>
                  <select className={`${field} h-9 w-auto min-w-[170px]`} value={condition.field}
                          onChange={e => updateCondition(index, blankCondition(fieldSpec(e.target.value)))}>
                    {objectSpec.fields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                  </select>
                  <select className={`${field} h-9 w-auto`} value={condition.operator} onChange={e => updateCondition(index, { operator: e.target.value })}>
                    {spec.operators.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <span className="min-w-[180px] flex-1">
                    {spec.type === "select" ? (
                      <select className={`${field} h-9`} value={condition.value} onChange={e => updateCondition(index, { value: e.target.value })}>
                        {spec.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input type={spec.type === "number" ? "number" : "text"} step="any" required className={`${field} h-9`} value={condition.value}
                             onChange={e => updateCondition(index, { value: e.target.value })} />
                    )}
                  </span>
                  <RemoveButton icon="fa-times" onClick={() => set({ conditions: state.conditions.filter((_, i) => i !== index) })} />
                </div>
              )
            })}
          </div>
          {objectSpec.fields.length > 0 ? (
            <button type="button" className={`!mt-3 ${link}`} onClick={() => set({ conditions: [...state.conditions, blankCondition(objectSpec.fields[0])] })}>
              <i className="fas fa-plus text-[10px]" />{t("form.add_condition")}
            </button>
          ) : (
            <p className="text-[12.5px] text-slate-400">{t("form.no_condition_fields")}</p>
          )}
          {props.legacyAllowed && (
            <details className="!mt-4 rounded-xl border border-dashed border-slate-200 !px-3 !py-2" open={legacyOpen}>
              <summary className="cursor-pointer text-[12px] text-slate-500">{t("form.legacy_conditions")}</summary>
              <input type="text" className={`${field} !mt-2 font-mono`} placeholder="active?,client.vip?" value={state.legacyConditions}
                     onChange={e => set({ legacyConditions: e.target.value })} />
            </details>
          )}
        </Step>

        <Step tone="bg-emerald-100 text-emerald-600" icon="fa-play" title={t("form.then_title")} hint={t("form.then_hint")}>
          <div className="space-y-3">
            {liveActions.map(action => (
              <ActionCard key={action.key} action={action} type={types[action.type]} object={eventSpec.object} objectRoles={objectSpec.roles}
                          props={props} update={change => updateAction(action.key, change)} remove={() => removeAction(action.key)} />
            ))}
          </div>

          {props.webhookAllowed && state.webhook.enabled && (
            <WebhookCard webhook={state.webhook} type={types.webhook} manual={eventSpec.manual} props={props}
                         update={change => set({ webhook: { ...state.webhook, ...change } })}
                         remove={() => set({ webhook: { ...state.webhook, enabled: false, hiddenByEvent: false } })} />
          )}

          <div className="!mt-4 flex flex-wrap gap-2 border-t border-slate-100 !pt-4">
            {eventSpec.actions.map(type => (
              <button key={type} type="button" onClick={() => set({ actions: [...state.actions, blankAction(type, props)] })}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white !py-1.5 !pl-1.5 !pr-3 text-[12.5px] font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50">
                <ActionIcon type={types[type]} />{types[type].label}
              </button>
            ))}
            {eventSpec.webhook && !state.webhook.enabled && (
              <button type="button" onClick={() => set({ webhook: { ...state.webhook, enabled: true } })}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white !py-1.5 !pl-1.5 !pr-3 text-[12.5px] font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50">
                <ActionIcon type={types.webhook} />{types.webhook.label}
              </button>
            )}
          </div>
        </Step>
      </div>

      <aside className="space-y-4 xl:sticky xl:top-4">
        <div className={card}>
          <h3 className="text-[12px] font-semibold uppercase tracking-wider text-slate-400">{t("form.summary")}</h3>
          <p className="!mt-2 text-[13px] leading-relaxed text-slate-700">
            {t("sentence.when")} <strong>{eventLabel.toLowerCase()}</strong>,{" "}
            {state.conditions.length > 0 && `${t("sentence.if").toLowerCase()} ${state.conditions.map(conditionText).join(` ${t("form.and").toLowerCase()} `)}, `}
            {actionNames.length > 0 ? `${t("sentence.then").toLowerCase()} ${actionNames.join(", ")}.` : t("form.nothing_yet")}
          </p>
          <div className="!mt-4 flex flex-col gap-2">
            <button type="submit" className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-lg bg-brand-500 !px-3.5 text-[13px] font-medium text-white transition hover:bg-brand-600">
              <i className="fas fa-check text-[11px]" />{t("form.save")}
            </button>
            <a href={props.cancelUrl} className="!py-1.5 text-center text-[12.5px] text-slate-500 hover:text-slate-800">{t("form.cancel")}</a>
          </div>
        </div>
        <div className="rounded-xl border border-blue-100 bg-blue-50/60 !p-4 text-[12px] leading-relaxed text-blue-900">
          <i className="fas fa-info-circle !mr-1" />{t("form.help")}
        </div>
      </aside>
    </form>
  )
}

export default AutomationForm
